import { Injectable, inject } from '@angular/core';
import { TODO } from '@b2b/types/core/utils';
import { Ng1StateDeclaration } from '@uirouter/angularjs';

import { ApiService } from '../../api/api.service';

interface FeatureObjectLockItem {
  ids: string[];
  views: string[];
}

/**
 * FeatureObjectLockService — Angular 18.
 *
 * Ported 1:1 from apps/b2b-ui/common/services/feature/objectLock/objectLock.service.ts
 * (legacy AngularJS name 'FeatureObjectLockService'). Tracks per-view object
 * locks (ids grouped by UI-Router state views). Delegates HTTP to the Angular 18
 * ApiService facade (`api.lock`).
 *
 * Internal to FeatureService (injected directly), so it is not downgrade-registered.
 * Wave 3 item #26.
 *
 * NOTE: `findView()` preserves the legacy index bug (`stateNames[i]` instead of
 * `stateNames[j]`). The existing call sites only ever pass single-item arrays,
 * so the behaviour is identical — kept verbatim to avoid changing semantics
 * under a migration PR.
 */
@Injectable({ providedIn: 'root' })
export class FeatureObjectLockService {
  readonly name = 'objectLock';
  readonly key = 'lockAlwaysRequiredToUpdateRequiresLockObjects';

  private apiService = inject(ApiService);

  api = this.apiService.lock;
  items: FeatureObjectLockItem[] = [];

  init() {
    this.reset();
    return true;
  }

  reset() {
    this.items = [];
  }

  add(props: { id: TODO; views: TODO[] }) {
    const found = this.findView(props.views);

    if (!found) {
      this.items.push({
        views: props.views,
        ids: [props.id],
      });
    } else if (!this.hasLock(found.view, props.id)) {
      const item = this.items[found.index];
      item.ids.push(props.id);
    }
  }

  remove(props: { id: TODO; view: string | undefined }) {
    const found = this.findView([props.view]);
    const foundId = this.hasLock(props.view, props.id);
    let r: TODO | false = false;

    if (found && foundId) {
      r = this.items[found.index].ids.length;

      const item = this.items[found.index];
      item.ids.splice(foundId.index, 1);

      if (item.ids.length <= 0) {
        this.items.splice(found.index, 1);
      }
    }

    return r;
  }

  /**
   * if `to` has object lock enabled.
   */
  isEligible(state: Ng1StateDeclaration, params: { [paramName: string]: any }) {
    return state.data && state.data.features && state.data.features.objectLock && state.data.features.objectLock.type;
  }

  hasLock(state: TODO, id: TODO) {
    if (state && id) {
      const found = this.findView([state]);
      const item = found ? this.items[found.index] : undefined;

      if (item && Array.isArray(item.ids)) {
        const foundIdIndex = item.ids.indexOf(id);

        if (foundIdIndex > -1) {
          return { index: foundIdIndex };
        }
      }
    }

    return false;
  }

  /**
   * if to is not in items[x].views list and if from has a lock right now
   */
  canRelease(sourceState: TODO, srcParams: TODO, destinationState: TODO, destParams: TODO) {
    const destinationName = destinationState && destinationState.name;
    return destinationName && !this.findView([destinationState.name]) && this.hasLock(sourceState.name, srcParams.id);
  }

  /*
   * Is the stateName part of any current views
   */
  includesView(list: TODO[], stateName: string) {
    list = list || [];

    for (let i = 0, ii = list.length; i < ii; i++) {
      const re = new RegExp('^' + list[i] + '.*');
      if (re.test(stateName)) {
        return list[i];
      }
    }

    return false;
  }

  findView(stateNames: TODO[]) {
    let r: { index: number; view: TODO } | false = false;

    for (let i = 0, ii = this.items.length; i < ii && !r; i++) {
      const views = this.items[i].views;
      if (Array.isArray(stateNames)) {
        for (let j = 0, jj = stateNames.length; j < jj; j++) {
          const testState = stateNames[i];
          const result = this.includesView(views, testState);

          if (result) {
            r = { index: i, view: result };
            break;
          }
        }
      }
    }

    return r;
  }
}
